import { useState, useCallback } from 'react';

interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

interface AdvancedFilterOption {
  value: string;
  label: string;
  count?: number;
}

interface AdvancedFilterConfig {
  key: string;
  label: string;
  type: 'select' | 'multiselect' | 'daterange' | 'number' | 'boolean';
  options?: AdvancedFilterOption[];
  placeholder?: string;
  min?: number;
  max?: number;
}

interface AdvancedFilterProps {
  filters: AdvancedFilterConfig[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onReset: () => void;
  className?: string;
}

export function AdvancedFilter({
  filters,
  values,
  onChange,
  onReset,
  className = '',
}: AdvancedFilterProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveFilters = Object.values(values).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some(v => v !== undefined && v !== '');
    }
    return value !== undefined && value !== '' && value !== null;
  });

  const renderFilterInput = useCallback((filter: AdvancedFilterConfig) => {
    const value = values[filter.key];

    switch (filter.type) {
      case 'select':
        return (
          <select
            className="form-control"
            value={value || ''}
            onChange={(e) => onChange(filter.key, e.target.value || undefined)}
          >
            <option value="">{filter.placeholder || `Select ${filter.label}`}</option>
            {filter.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {option.count !== undefined && ` (${option.count})`}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="multiselect-container">
            <select
              className="form-control"
              multiple
              size={Math.min(filter.options?.length || 3, 5)}
              value={selectedValues}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                onChange(filter.key, selected.length > 0 ? selected : undefined);
              }}
            >
              {filter.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                  {option.count !== undefined && ` (${option.count})`}
                </option>
              ))}
            </select>
            <small className="form-text text-muted">
              Hold Ctrl/Cmd to select multiple items
            </small>
          </div>
        );

      case 'daterange':
        const dateRange = value as DateRangeFilter || {};
        return (
          <div className="row">
            <div className="col-6">
              <input
                type="date"
                className="form-control"
                placeholder="Start Date"
                value={dateRange.startDate || ''}
                onChange={(e) => onChange(filter.key, {
                  ...dateRange,
                  startDate: e.target.value || undefined
                })}
              />
            </div>
            <div className="col-6">
              <input
                type="date"
                className="form-control"
                placeholder="End Date"
                value={dateRange.endDate || ''}
                onChange={(e) => onChange(filter.key, {
                  ...dateRange,
                  endDate: e.target.value || undefined
                })}
              />
            </div>
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            className="form-control"
            placeholder={filter.placeholder || filter.label}
            value={value || ''}
            min={filter.min}
            max={filter.max}
            onChange={(e) => onChange(filter.key, e.target.value ? Number(e.target.value) : undefined)}
          />
        );

      case 'boolean':
        return (
          <select
            className="form-control"
            value={value === undefined ? '' : value.toString()}
            onChange={(e) => {
              const val = e.target.value;
              onChange(filter.key, val === '' ? undefined : val === 'true');
            }}
          >
            <option value="">{filter.placeholder || `Select ${filter.label}`}</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      default:
        return (
          <input
            type="text"
            className="form-control"
            placeholder={filter.placeholder || filter.label}
            value={value || ''}
            onChange={(e) => onChange(filter.key, e.target.value || undefined)}
          />
        );
    }
  }, [values, onChange]);

  return (
    <div className={`advanced-filter ${className}`}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="fas fa-filter mr-2"></i>
              Advanced Filters
              {hasActiveFilters && (
                <span className="badge badge-primary ml-2">
                  {Object.values(values).filter(v => {
                    if (Array.isArray(v)) return v.length > 0;
                    if (typeof v === 'object' && v !== null) {
                      return Object.values(v).some(val => val !== undefined && val !== '');
                    }
                    return v !== undefined && v !== '' && v !== null;
                  }).length}
                </span>
              )}
            </h6>
            <div>
              {hasActiveFilters && (
                <button
                  className="btn btn-sm btn-outline-secondary mr-2"
                  onClick={onReset}
                  title="Reset all filters"
                >
                  <i className="fas fa-times mr-1"></i>
                  Reset
                </button>
              )}
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setIsExpanded(!isExpanded)}
                title={isExpanded ? 'Collapse filters' : 'Expand filters'}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
              </button>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="card-body">
            <div className="row">
              {filters.map((filter) => (
                <div key={filter.key} className="col-md-6 col-lg-4 mb-3">
                  <label className="form-label font-weight-bold">
                    {filter.label}
                  </label>
                  {renderFilterInput(filter)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}